import React, {useEffect, useRef, useState} from "react";
import "./App.scss";
import Nav from "./components/Nav/Nav";
import Masthead from "./components/Masthead/Masthead";
import Hero from "./components/Hero/Hero";
import DownloadApp from "./components/DownloadApp/DownloadApp";
import Footnotes from "./components/Footnotes/Footnotes";
import Footer from "./components/Footer/Footer";
import useDeviceDetect from "./hooks/use-is-device-detect";
import {trackScrolling} from './hooks/tagging';
import {addExitClickListeners, closeExitOverlay} from "./components/ExitOverlay/ExitOverlayLogic";
import ExitOverlay from "./components/ExitOverlay/ExitOverlay";
import TileContainer from "./components/TileContainer/TileContainer";
import Questions from "./components/Questions/Questions";
import StickyFooter from "./components/StickyFooter/StickyFooter";
import { Helmet } from 'react-helmet-async';
import { initializeTest } from "./libs/cookie";

declare global {
    interface Window {
        OneTrust: any;
    }
}

function App() {
    const [loaded, setLoaded] = useState(false);
    const [test, setTest] = useState(false) // Keep this as false for A/B testing
    const isMobile = useDeviceDetect().isMobile;
    const [userStylesApplied, setUserStylesApplied] = useState(false);
    const [childHeight, setChildHeight] = useState(null);
    const [variant, setVariant] = useState('');
    // control = a, test = b

    const [testPEl, setTestPEl] = useState<HTMLParagraphElement | null>(null);
    const testPRef = (node: HTMLParagraphElement | null) => {
        setTestPEl(node)
    };
    
    // Mutes all the logs for production.  Comment out in development
    if (process.env.NODE_ENV === "production") {
        console.log = function() {};
    }


    const queryParams = new URLSearchParams(window.location.search)
    const focus = queryParams.get("focus")!

    let contentOrder: any = {
        english: {
            default: [0, 7, 2, 3, 4],
            subscriptions: [0, 1, 2, 3, 4],
            spending: [2, 1, 0, 3, 4],
            insights: [3, 1, 0, 2, 4],
            savings: [4, 1, 0, 2, 3],
            startsmart: [0, 1, 2, 3, 4],
            cardonoff: [5, 1, 0, 6, 4] // 5 and 6 are only used for cardonoff
        },
        spanish: {
            default: [1, 3, 4, 2, 5],
            creditscore: [3, 1, 4, 2, 0],
            savings: [4, 1, 3, 2, 0],
            spending: [5, 3, 4, 1, 0],
        },
    }

    contentOrder = process.env.REACT_APP_LANG === 'spanish' ? contentOrder.spanish : contentOrder.english;



    const headerContent: any = document.getElementsByTagName('HTML')[0].getAttribute('lang') === 'es'
        ? {
            default: {
                title: "Maneja tu dinero a tu manera con Wells Fargo",
                description: "Desde pequeña aprendiste la importancia de manejar tu dinero. Con Wells Fargo, tienes las herramientas para hacerlo a tu manera, con confianza y control.",
                canonicalURL: "https://welcome.wf.com/atumanera/",
                ogImage: "https://www17.wellsfargomedia.com/assets/images/icons/icon-hires_192x192.png",
                ogImageAlt: "Logotipo de Wells Fargo",
                twitterCard: "summary_large_image",
                twitterSite: "@WellsFargo"
            },
            creditscore: {
                title: "Establece alertas de Wells Fargo para estar al tanto de tu cuenta",
                description: "Las alertas de fraude de Wells Fargo te ayudan a mantener tu cuenta segura. Activa alertas de transacciones para estar siempre al tanto de tus movimientos financieros",
                canonicalURL: "https://welcome.wf.com/atumanera/?focus=alertas",
                ogImage: "https://www17.wellsfargomedia.com/assets/images/icons/icon-hires_192x192.png",
                ogImageAlt: "Logotipo de Wells Fargo",
                twitterCard: "summary_large_image",
                twitterSite: "@WellsFargo"
            },
            savings: {
                title: "Establece y monitorea tus ahorros fácilmente con Wells Fargo",
                description: "Transfiere dinero fácilmente entre tus cuentas de Wells Fargo con transferencias automáticas de ahorro. Ahorra sin esfuerzo y mantén tus finanzas organizadas.",
                canonicalURL: "https://welcome.wf.com/atumanera/?focus=ahorros",
                ogImage: "https://www17.wellsfargomedia.com/assets/images/icons/icon-hires_192x192.png",
                ogImageAlt: "Wells Fargo logo",
                twitterCard: "summary_large_image",
                twitterSite: "@WellsFargo"
            },
            spending: {
                title: "Controla tus gastos y maneja tu dinero con Wells Fargo",
                description: "Aprende a manejar tu dinero a tu manera. Monitorea tus gastos por categoría y mes a mes con la app de Wells Fargo para mantener tu presupuesto bajo control.",
                canonicalURL: "https://welcome.wf.com/atumanera/?focus=gasto",
                ogImage: "https://www17.wellsfargomedia.com/assets/images/icons/icon-hires_192x192.png",
                ogImageAlt: "Logotipo de Wells Fargo",
                twitterCard: "summary_large_image",
                twitterSite: "@WellsFargo"
            },
        }
        : {
            default: {
                title: "Do Money Your Way | Track, Save and Earn - Wells Fargo Money Smart",
                description: "Member FDIC. Improve your financial confidence with tools to track spending, prevent fraud, and grow your savings. Start strong with a $325 Wells Fargo promotional bonus.",
                canonicalURL: "https://welcome.wf.com/moneysmart/",
                ogImage: "https://www17.wellsfargomedia.com/assets/images/icons/icon-hires_192x192.png",
                ogImageAlt: "Wells Fargo logo",
                twitterCard: "summary_large_image",
                twitterSite: "@WellsFargo"
            },
            subscriptions: {
                title: "Easily Track Your Subscriptions | Wells Fargo Money Smart",
                description: "Stay on top of recurring charges with subscription tracking and the Wells Fargo app",
                canonicalURL: "https://welcome.wf.com/moneysmart/?focus=subscriptions",
                ogImage: "https://www17.wellsfargomedia.com/assets/images/icons/icon-hires_192x192.png",
                ogImageAlt: "Wells Fargo logo",
                twitterCard: "summary_large_image",
                twitterSite: "@WellsFargo"
            },
            spending: {
                title: "Track Your Spending Like a Pro | Personalized Money Support | Wells Fargo Money Smart",
                description: "Want to manage your money your way? We've got you. Whether you're tracking spending or planning ahead, we just help to get you where you want to go.",
                canonicalURL: "https://welcome.wf.com/moneysmart/?focus=spending",
                ogImage: "https://www17.wellsfargomedia.com/assets/images/icons/icon-hires_192x192.png",
                ogImageAlt: "Wells Fargo logo",
                twitterCard: "summary_large_image",
                twitterSite: "@WellsFargo"
            },
            insights: {
                title: "Smart Saving Starts Here | Support for Your Unique Money Style | Wells Fargo Money Smart",
                description: "Ready to get shout outs for saving regularly? We're here to support your unique approach to money. Wherever you want to go financially, we'll help you get there.",
                canonicalURL: "https://welcome.wf.com/moneysmart/?focus=insights",
                ogImage: "https://www17.wellsfargomedia.com/assets/images/icons/icon-hires_192x192.png",
                ogImageAlt: "Wells Fargo logo",
                twitterCard: "summary_large_image",
                twitterSite: "@WellsFargo"
            },
            savings: {
                title: "Make your money work for you | Wells Fargo Money Smart",
                description: "Stay on top of your savings goals with the Wells Fargo app, you will get the tools and support to help you manage your money your way. ",
                canonicalURL: "https://welcome.wf.com/moneysmart/?focus=savings",
                ogImage: "https://www17.wellsfargomedia.com/assets/images/icons/icon-hires_192x192.png",
                ogImageAlt: "Wells Fargo logo",
                twitterCard: "summary_large_image",
                twitterSite: "@WellsFargo"
            },
            startsmart: {
                title: "Smarter banking for students and new beginner bank accounts | Wells Fargo Money Smart",
                description: "Member FDIC. Get started with friendly clear access banking, no overdraft fees, easy money tracking and $125 bonus to start ",
                canonicalURL: "https://welcome.wf.com/moneysmart/?focus=startsmart",
                ogImage: "https://www17.wellsfargomedia.com/assets/images/icons/icon-hires_192x192.png",
                ogImageAlt: "Wells Fargo logo",
                twitterCard: "summary_large_image",
                twitterSite: "@WellsFargo"
            },
            cardonoff: {
                title: "Instantly turn your card on and off control your debit card in real time | Wells Fargo Money Smart",
                description: "Control your debit card in real time turn it off if lost or stolen then back when found.",
                canonicalURL: "https://welcome.wf.com/moneysmart/?focus=cardonoff",
                ogImage: "https://www17.wellsfargomedia.com/assets/images/icons/icon-hires_192x192.png",
                ogImageAlt: "Wells Fargo logo",
                twitterCard: "summary_large_image",
                twitterSite: "@WellsFargo"
            },
        }

    let contentString: string = "";

    if (process.env.REACT_APP_LANG === 'spanish') {
        // atumanera
        // alertas
        // ahorros
        // gasto

        switch (focus) {
            case "":
                contentString = "default";
                break;
            case "alertas":
                contentString = "creditscore";
                break;
            case contentString = "ahorros":
                contentString = "savings";
                break;
            case contentString = "gasto":
                contentString = "spending";
                break;
            default:
                contentString = "default";
                break;
        }
    }
    else {
        contentString = (contentOrder.hasOwnProperty(focus)) ? focus : "default";
    }


    console.log(contentOrder[contentString])
    const contentIndex: number = contentOrder[contentString][0];
    const isEvergreen = (contentString == 'default');

    let tileHeightOpen = () => window.innerWidth < 1080 ? 280 : 560
    let tileHeightClosed = () => window.innerWidth < 1080 ? 210 : 456


    // Can/should we put this in a lib?
    useEffect(() => {
        if (!loaded) {
            trackScrolling();
            setLoaded(true);
        }
        else {

            if (testPEl) {
                const interval = setInterval(() => {
                        const isApplied = getComputedStyle(testPEl).letterSpacing !== '-100px';
                        if (isApplied) {
                            setUserStylesApplied(true)
                            clearInterval(interval);
                            const footer = document.querySelector<HTMLElement>(`#footer`);
                            const stickyFooter = document.querySelector<HTMLElement>(`#stickyFooter`);
                            footer!.style.paddingBottom = getComputedStyle(stickyFooter!).height;
                            const tiles: NodeListOf<HTMLElement> = document.querySelectorAll('[class^="Tile_tile"] h2');
                            tiles.forEach(tile => {
                                    tile.style.fontSize = window.innerWidth < 1080 ? "" : "26px"


                                }
                            )

                        }
                    }, 1000
                )

                if (window.innerWidth < 500) {
                    //@ts-ignore
                    tileHeightOpen = () => 600
                }
            }
        }
    }, [loaded, testPEl, childHeight]);

    console.log('variant ', variant)

    return (
        <div className="App">
            <Helmet>
                <title>{headerContent[contentString]['title']}</title>
                <meta name="description" content={headerContent[contentString]['description']}/>
                <meta name="twitter:card" content="summary_large_image"/>
                <meta name="twitter:site" content="@WellsFargo"/>
                <meta property="og:title" content={headerContent[contentString]['title']}/>
                <meta property="og:description" content={headerContent[contentString]['description']}/>
                <meta property="og:url" content={headerContent[contentString]['canonicalURL']}/>
                <meta property="og:image" content={headerContent[contentString]['ogImage']}/>
                <meta property="og:image:alt" content={headerContent[contentString]['ogImageAlt']}/>
                <meta property="og:type" content="website"/>
                <meta property="og:site_name" content="wellsfargo.com"/>
                <link rel="canonical" href={headerContent[contentString]['canonicalURL']}/>
            </Helmet>
            <Nav/>
            {/* <p ref={testPRef} style={{letterSpacing: "-100px", display: "none"}} aria-hidden={true}>Once Upon a Midnight Dreary</p> */}
            <main>
                <Masthead
                    contentIndex={contentIndex}
                    isEvergreen={isEvergreen}
                    isStartSmart={contentString === "startsmart"}
                    focus={contentString}
                />
                <Hero
                    isStartSmart={contentString === "startsmart"}
                />
                <TileContainer
                    contentOrder={contentOrder[contentString]}
                    isStartSmart={contentString === "startsmart"}
                    isCardOnOff={contentString === "spending"}
                    userStylesApplied={userStylesApplied}
                    focus={contentString}
                />
                {/*TODO phase out content string*/}
                <DownloadApp
                    contentString={contentString}
                />
                {/*TODO phase out content string*/}
                <Questions/>
                {/*TODO phase out content string*/}
                <Footnotes
                    contentString={contentString}
                />
                {/*TODO phase out content string*/}
                <Footer id="footer" isMobile={isMobile}/>
                <StickyFooter version={contentString === "startsmart" ? "startsmart" : "default"} focus={contentString} variant={variant}/>
                <ExitOverlay
                    closeExitOverlay={closeExitOverlay}
                    addExitClickListeners={addExitClickListeners}
                />
            </main>
        </div>
    );
}

export default App;
